import { useCallback, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  createDocumentServiceClient,
  validateDocumentFile,
  type CompletedDocumentUpload,
  type DocumentUploadContentType,
  type DocumentUploadProgress,
} from "./client";

export type DocumentUploadStatus = "ready" | "preparing" | "uploading" | "finalizing" | "success" | "error";
export type DocumentUploadItemStatus = "waiting" | "uploading" | "finalizing" | "complete" | "failed";
export type DocumentUploadSelection = {
  file: File;
  filename?: string;
  folderId?: string | null;
};
export type DocumentUploadItem = {
  id: string;
  filename: string;
  size: number;
  status: DocumentUploadItemStatus;
  error: string | null;
};

const DOCUMENT_UPLOAD_CONCURRENCY = 3;

export function useDocumentUpload() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.documentService?.apiBaseUrl;
  const [status, setStatus] = useState<DocumentUploadStatus>("ready");
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<CompletedDocumentUpload | null>(null);
  const [progress, setProgress] = useState<DocumentUploadProgress | null>(null);
  const [items, setItems] = useState<DocumentUploadItem[]>([]);
  const activeUpload = useRef(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createDocumentServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const reset = useCallback(() => {
    setStatus("ready");
    setError(null);
    setCompleted(null);
    setProgress(null);
    setItems([]);
    activeUpload.current = false;
  }, []);

  const uploadSingleDocument = useCallback(
    async (
      file: File,
      filename: string,
      options: {
        folderId?: string | null;
        onProgress?: (progress: DocumentUploadProgress) => void;
        onStatus?: (status: Extract<DocumentUploadStatus, "preparing" | "uploading" | "finalizing">) => void;
      } = {},
    ) => {
      const validationError = validateDocumentFile(file);
      if (validationError) {
        throw new Error(validationError);
      }

      if (!client) {
        throw new Error("Health Documents are not available yet.");
      }

      options.onStatus?.("preparing");
      const payload = {
        filename,
        content_type: file.type as DocumentUploadContentType,
        size_bytes: file.size,
        ...(options.folderId && options.folderId !== "root" ? { folder_id: options.folderId } : {}),
      };
      const upload = await client.createUpload(payload);

      options.onStatus?.("uploading");
      await client.uploadFileToPresignedUrl(upload, file, options.onProgress);
      options.onStatus?.("finalizing");
      return client.completeUpload(upload.document_id);
    },
    [client],
  );

  const uploadDocument = useCallback(
    async (file: File, filename = file.name, folderId?: string | null) => {
      if (activeUpload.current) {
        return null;
      }

      const validationError = validateDocumentFile(file);
      if (validationError) {
        setError(validationError);
        setProgress(null);
        setStatus("error");
        return null;
      }

      if (!client) {
        setError("Health Documents are not available yet.");
        setProgress(null);
        setStatus("error");
        return null;
      }

      activeUpload.current = true;
      setError(null);
      setCompleted(null);
      setProgress(null);
      setItems([{ id: `0-${file.name}-${file.size}-${file.lastModified}`, filename, size: file.size, status: "waiting", error: null }]);

      try {
        const nextCompleted = await uploadSingleDocument(file, filename, {
          folderId,
          onProgress: setProgress,
          onStatus: (nextStatus) => {
            setStatus(nextStatus);
            setItems((currentItems) =>
              currentItems.map((item) => ({
                ...item,
                status: nextStatus === "finalizing" ? "finalizing" : "uploading",
                error: null,
              })),
            );
          },
        });
        setCompleted(nextCompleted);
        setItems((currentItems) => currentItems.map((item) => ({ ...item, status: "complete", error: null })));
        setStatus("success");
        return nextCompleted;
      } catch (nextError) {
        const message = nextError instanceof Error ? nextError.message : "We could not upload your document right now.";
        setError(message);
        setItems((currentItems) => currentItems.map((item) => ({ ...item, status: "failed", error: message })));
        setStatus("error");
        return null;
      } finally {
        activeUpload.current = false;
      }
    },
    [client, uploadSingleDocument],
  );

  const uploadDocuments = useCallback(
    async (uploads: DocumentUploadSelection[]) => {
      if (activeUpload.current) {
        return [];
      }

      if (!uploads.length) {
        setError("Choose a PDF, JPEG or PNG document to upload.");
        setStatus("error");
        return [];
      }

      if (!client) {
        setError("Health Documents are not available yet.");
        setProgress(null);
        setStatus("error");
        return [];
      }

      activeUpload.current = true;
      setError(null);
      setCompleted(null);
      setProgress(null);
      setStatus("preparing");

      const initialItems = uploads.map(({ file, filename }, index) => ({
        id: `${index}-${file.name}-${file.size}-${file.lastModified}`,
        filename: filename ?? file.name,
        size: file.size,
        status: "waiting" as const,
        error: null,
      }));
      setItems(initialItems);

      const results: Array<CompletedDocumentUpload | null> = Array.from({ length: uploads.length }, () => null);
      let nextIndex = 0;

      const updateItem = (index: number, update: Partial<DocumentUploadItem>) => {
        setItems((currentItems) => currentItems.map((item, itemIndex) => (itemIndex === index ? { ...item, ...update } : item)));
      };

      const worker = async () => {
        for (;;) {
          const index = nextIndex;
          nextIndex += 1;
          if (index >= uploads.length) {
            return;
          }

          const { file, filename, folderId } = uploads[index];
          const resolvedFilename = filename ?? file.name;

          try {
            const nextCompleted = await uploadSingleDocument(file, resolvedFilename, {
              folderId,
              onStatus: (nextStatus) => {
                updateItem(index, { status: nextStatus === "finalizing" ? "finalizing" : "uploading", error: null });
              },
            });
            results[index] = nextCompleted;
            updateItem(index, { status: "complete" });
          } catch (nextError) {
            updateItem(index, {
              status: "failed",
              error: nextError instanceof Error ? nextError.message : "We could not upload this document right now.",
            });
          }
        }
      };

      try {
        setStatus("uploading");
        await Promise.all(Array.from({ length: Math.min(DOCUMENT_UPLOAD_CONCURRENCY, uploads.length) }, () => worker()));
        const successfulUploads = results.filter((result): result is CompletedDocumentUpload => result !== null);
        const failedUploads = uploads.length - successfulUploads.length;
        setCompleted(successfulUploads.at(-1) ?? null);
        setStatus(failedUploads > 0 ? "error" : "success");
        setError(failedUploads > 0 ? `${failedUploads} ${failedUploads === 1 ? "file" : "files"} failed to upload.` : null);
        return successfulUploads;
      } finally {
        activeUpload.current = false;
      }
    },
    [client, uploadSingleDocument],
  );

  return {
    completed,
    error,
    items,
    progress,
    reset,
    status,
    uploadDocument,
    uploadDocuments,
  };
}
